import React, { useEffect, useRef, useState } from "react";

import { SKSMediaChooserBlock } from "@reactivated";

import { CarouselApi } from "../Carousel/Carousel";
import type { MediaChooserBlock } from "../MediaChooserBlock";
import type { VideoAttrs } from "../VideoPlayerControls";

type SlideMedia = SKSMediaChooserBlock | null | undefined;
type VideoProps = React.ComponentProps<typeof MediaChooserBlock>["videoProps"];

/**
 * Media whose video should drive the carousel's advance timing (play
 * through once, advance on end). Press-to-play videos are excluded — they'd
 * hold the carousel forever waiting on a play that may never come.
 */
export function isAutoplayVideoMedia(slideMedia: SlideMedia): boolean {
    const media = slideMedia?.[0];
    if (!media) return false;
    // Legacy videos render with autoplay behavior in 2026 blocks (no `type`
    // prop is passed to MediaChooserBlock)
    if (media.type === "video") return true;
    if (media.type === "video_with_options") {
        return (
            media.value.playback_behavior === "autoplay" && !!media.value.video
        );
    }
    return false;
}

type VideoPlaybackPhase = "playing" | "ended";

/**
 * Paces a Carousel's auto-advance by its slides' autoplay videos: a video
 * slide holds the carousel until its video ends, every other slide keeps
 * the interval timer.
 *
 * Wiring: pass `carouselApi` to `apiRef`, `carouselContainer` via
 * `containerProps`, and `videoPacesAutoPlay` / `onActiveChange` /
 * `onAutoPlayRunningChange` to their same-named Carousel props; give each
 * slide's MediaChooserBlock `videoPropsFor(index)`. Feed
 * `isAutoPlayRunning` (negated) into MotionPauseContext so the videos
 * pause in lockstep with rotation.
 */
export function useVideoPacedAutoPlay({
    slideMedia,
    enabled,
}: {
    slideMedia: ReadonlyArray<SlideMedia>;
    enabled: boolean;
}) {
    const [activeIndex, setActiveIndex] = useState(0);
    // Combined autoplay state; false until the carousel reports on mount
    const [isAutoPlayRunning, setIsAutoPlayRunning] = useState(false);
    // Playback phases for the video slides currently on screen.
    // (There can be more than one when slides are in transition.)
    // An entry appears once a video has actually started playing and is deleted
    // when its slide deactivates, so a revisited slide starts fresh.
    // Until a video has an entry, the carousel's fixed timer stays in charge;
    // this prevents a broken or never-starting video from breaking autoplay
    const [activeVideoPhases, setActiveVideoPhases] = useState<
        ReadonlyMap<number, VideoPlaybackPhase>
    >(new Map());

    const carouselApi = useRef<CarouselApi>(null);
    const carouselContainer = useRef<HTMLDivElement>(null);
    const videoElements = useRef(new Map<number, HTMLVideoElement>());
    const activeIndexRef = useRef(0);

    const videoPacesAutoPlay =
        enabled &&
        isAutoplayVideoMedia(slideMedia[activeIndex]) &&
        activeVideoPhases.get(activeIndex) === "playing";

    /**
     * When videoPacesAutoPlay stands the Carousel's timer down, this
     * stops Carousel from writing --autoplay-progress, so we do it here.
     */
    useEffect(() => {
        if (!videoPacesAutoPlay) return;
        let frameId: number;
        const writeProgress = () => {
            const video = videoElements.current.get(activeIndexRef.current);
            if (video && video.duration > 0) {
                const progress = Math.min(
                    video.currentTime / video.duration,
                    1,
                );
                carouselContainer.current?.style.setProperty(
                    "--autoplay-progress",
                    String(progress),
                );
            }
            frameId = requestAnimationFrame(writeProgress);
        };
        frameId = requestAnimationFrame(writeProgress);
        return () => cancelAnimationFrame(frameId);
    }, [videoPacesAutoPlay]);

    const setVideoPhase = (index: number, phase: VideoPlaybackPhase) => {
        setActiveVideoPhases((previous) => {
            if (previous.get(index) === phase) return previous;
            const next = new Map(previous);
            next.set(index, phase);
            return next;
        });
    };

    const handleVideoEnded = (index: number) => {
        setVideoPhase(index, "ended");
        if (index === activeIndexRef.current) {
            // No-op if rotation is effectively paused (e.g. keyboard focus);
            // onAutoPlayRunningChange performs the advance on resume.
            carouselApi.current?.autoAdvance();
        }
    };

    const onAutoPlayRunningChange = (isRunning: boolean) => {
        setIsAutoPlayRunning(isRunning);
        if (
            isRunning &&
            activeVideoPhases.get(activeIndexRef.current) === "ended"
        ) {
            carouselApi.current?.autoAdvance();
        }
    };

    const onActiveChange = (indexes: Set<number>) => {
        const first = indexes.values().next().value;
        // Stay on the current slide while it's still visible (sticky through
        // transitions); when it leaves, whichever slide has been visible
        // longest takes over (usually the only one left)
        if (first === undefined || indexes.has(activeIndexRef.current)) return;
        const previousIndex = activeIndexRef.current;
        activeIndexRef.current = first;
        setActiveIndex(first);
        // Rewind the outgoing slide's video (and clear its phase, below) so
        // a revisit replays it from the start
        const previousVideo = videoElements.current.get(previousIndex);
        if (previousVideo) previousVideo.currentTime = 0;
        // Clear the incoming slide's phase too: a late `ended` from a prior
        // visit (queued behind this active-change) can reinsert an entry
        // after the outgoing cleanup, and a stale phase would stop the video
        // from pacing this visit. Every visit starts with no entry.
        setActiveVideoPhases((previous) => {
            if (!previous.has(previousIndex) && !previous.has(first)) {
                return previous;
            }
            const next = new Map(previous);
            next.delete(previousIndex);
            next.delete(first);
            return next;
        });
    };

    const buildVideoAttrs = (index: number): VideoAttrs => ({
        // Overrides VideoPlayerControls' loop default for autoplay videos.
        // The advance relies on `ended` firing, and a looping video never ends.
        loop: false,
        onTimeUpdate: (event) => {
            videoElements.current.set(index, event.currentTarget);
            // A video that plays one frame and immediately pauses (e.g. a
            // play/pause race in VideoPlayerControls) still fires timeupdate;
            // require sustained playback before freezing the fallback timer
            if (
                !activeVideoPhases.has(index) &&
                event.currentTarget.currentTime > 0.25
            ) {
                setVideoPhase(index, "playing");
            }
        },
        onEnded: (event) => {
            videoElements.current.set(index, event.currentTarget);
            handleVideoEnded(index);
        },
    });

    const videoPropsFor = (index: number): VideoProps =>
        enabled && isAutoplayVideoMedia(slideMedia[index])
            ? { attrs: buildVideoAttrs(index) }
            : undefined;

    return {
        carouselApi,
        carouselContainer,
        activeIndex,
        isAutoPlayRunning,
        videoPacesAutoPlay,
        onActiveChange,
        onAutoPlayRunningChange,
        videoPropsFor,
    };
}
